import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtDateTime } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Users, UserPlus, KeyRound, Trash2, ShieldCheck, User as UserIcon } from "lucide-react";
import { toast } from "sonner";

const ROLLEN = [
  { value: "admin", label: "Admin" },
  { value: "gebruiker", label: "Gebruiker" },
];

function err(e, fallback) {
  const d = e?.response?.data?.detail;
  return typeof d === "string" ? d : fallback;
}

function NieuwGebruikerDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ naam: "", email: "", rol: "gebruiker", wachtwoord: "", beldoel: 20 });
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!f.naam.trim() || !f.email.trim()) { toast.error("Naam en e-mail zijn verplicht"); return; }
    if ((f.wachtwoord || "").length < 6) { toast.error("Wachtwoord minimaal 6 tekens"); return; }
    setBusy(true);
    try {
      await api.post("/gebruikers", { ...f, beldoel: Number(f.beldoel) || 20 });
      toast.success("Gebruiker aangemaakt");
      setOpen(false);
      setF({ naam: "", email: "", rol: "gebruiker", wachtwoord: "", beldoel: 20 });
      onCreated();
    } catch (e) { toast.error(err(e, "Aanmaken mislukt")); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="nieuwe-gebruiker-btn"><UserPlus className="h-4 w-4 mr-1" /> Nieuwe gebruiker</Button>
      </DialogTrigger>
      <DialogContent data-testid="nieuwe-gebruiker-dialog">
        <DialogHeader><DialogTitle>Nieuwe gebruiker</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Naam</Label><Input data-testid="gebruiker-naam" value={f.naam} onChange={(e) => setF({ ...f, naam: e.target.value })} /></div>
          <div><Label>E-mail</Label><Input type="email" data-testid="gebruiker-email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Rol</Label>
              <Select value={f.rol} onValueChange={(v) => setF({ ...f, rol: v })}>
                <SelectTrigger data-testid="gebruiker-rol"><SelectValue /></SelectTrigger>
                <SelectContent>{ROLLEN.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Beldoel/dag</Label><Input type="number" min={0} max={1000} value={f.beldoel} onChange={(e) => setF({ ...f, beldoel: e.target.value })} /></div>
          </div>
          <div><Label>Wachtwoord</Label><Input type="text" data-testid="gebruiker-wachtwoord" value={f.wachtwoord} onChange={(e) => setF({ ...f, wachtwoord: e.target.value })} placeholder="min. 6 tekens" /></div>
        </div>
        <DialogFooter>
          <Button onClick={save} disabled={busy} data-testid="gebruiker-opslaan">{busy ? "Bezig…" : "Aanmaken"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ResetWachtwoordDialog({ gebruiker, onDone }) {
  const [open, setOpen] = useState(false);
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (pw.length < 6) { toast.error("Wachtwoord minimaal 6 tekens"); return; }
    setBusy(true);
    try {
      await api.post(`/gebruikers/${gebruiker.id}/wachtwoord`, { wachtwoord: pw });
      toast.success(`Wachtwoord gereset voor ${gebruiker.naam}`);
      setOpen(false); setPw(""); onDone?.();
    } catch (e) { toast.error(err(e, "Reset mislukt")); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="icon" className="h-8 w-8" title="Wachtwoord resetten"
          data-testid={`reset-pw-${gebruiker.id}`}><KeyRound className="h-4 w-4" /></Button>
      </DialogTrigger>
      <DialogContent data-testid="reset-pw-dialog">
        <DialogHeader><DialogTitle>Wachtwoord resetten — {gebruiker.naam}</DialogTitle></DialogHeader>
        <div className="space-y-2">
          <Label>Nieuw wachtwoord</Label>
          <Input type="text" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="min. 6 tekens"
            data-testid="reset-pw-input" />
        </div>
        <DialogFooter>
          <Button onClick={save} disabled={busy} data-testid="reset-pw-opslaan">{busy ? "Bezig…" : "Wachtwoord instellen"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function InstellingenGebruikers() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/gebruikers"); setUsers(data || []); }
    catch (e) { toast.error(err(e, "Laden mislukt")); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const changeRol = async (u, rol) => {
    try { await api.patch(`/gebruikers/${u.id}`, { rol }); toast.success("Rol bijgewerkt"); load(); }
    catch (e) { toast.error(err(e, "Bijwerken mislukt")); }
  };

  const remove = async (u) => {
    if (!window.confirm(`Gebruiker "${u.naam}" verwijderen?`)) return;
    try { await api.delete(`/gebruikers/${u.id}`); toast.success("Verwijderd"); load(); }
    catch (e) { toast.error(err(e, "Verwijderen mislukt")); }
  };

  return (
    <div className="space-y-6 max-w-4xl" data-testid="instellingen-gebruikers-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-sm text-muted-foreground">Instellingen</div>
          <h1 className="font-display text-4xl font-medium tracking-tight mt-1">Gebruikers</h1>
          <p className="text-sm text-muted-foreground mt-1">Maak gebruikers aan, beheer rechten en reset wachtwoorden.</p>
        </div>
        <NieuwGebruikerDialog onCreated={load} />
      </div>

      <div className="bg-card border border-border rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-secondary/50 text-muted-foreground text-xs uppercase tracking-wide">
            <tr>
              <th className="text-left font-medium px-4 py-3">Gebruiker</th>
              <th className="text-left font-medium px-4 py-3 w-40">Rol</th>
              <th className="text-left font-medium px-4 py-3 w-28">Beldoel</th>
              <th className="text-left font-medium px-4 py-3 w-44">Aangemaakt</th>
              <th className="px-4 py-3 w-24" />
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">Laden…</td></tr>
            ) : users.map((u) => (
              <tr key={u.id} className="border-t border-border" data-testid={`gebruiker-rij-${u.id}`}>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    <div className={`h-9 w-9 rounded-full flex items-center justify-center ${u.rol === "admin" ? "bg-primary/10 text-primary" : "bg-secondary text-muted-foreground"}`}>
                      {u.rol === "admin" ? <ShieldCheck className="h-4 w-4" /> : <UserIcon className="h-4 w-4" />}
                    </div>
                    <div>
                      <div className="font-medium flex items-center gap-2">
                        {u.naam}
                        {u.id === user?.id && <Badge variant="outline" className="text-xs">jij</Badge>}
                      </div>
                      <div className="text-xs text-muted-foreground">{u.email}</div>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <Select value={u.rol || "gebruiker"} onValueChange={(v) => changeRol(u, v)}>
                    <SelectTrigger className="h-8 w-32" data-testid={`rol-select-${u.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{ROLLEN.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
                  </Select>
                </td>
                <td className="px-4 py-3 text-muted-foreground">{u.beldoel ?? 20}/dag</td>
                <td className="px-4 py-3 text-muted-foreground">{fmtDateTime(u.created_at)}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-1">
                    <ResetWachtwoordDialog gebruiker={u} onDone={load} />
                    {u.id !== user?.id && (
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-red-600 hover:text-red-700"
                        onClick={() => remove(u)} title="Verwijderen" data-testid={`delete-gebruiker-${u.id}`}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {!loading && users.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">Geen gebruikers.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
